<template>
  <div class="warehouse-page">
    <div class="mode-switch">
      <button type="button" :class="{ active: panel === 'items' }" @click="panel = 'items'">
        <i class="fa-solid fa-tags"></i>
        <span>
          <strong>Product master</strong>
          <small>What the product is — price, UOM, barcode (not stock per warehouse)</small>
        </span>
      </button>
      <button type="button" :class="{ active: panel === 'stock' }" @click="panel = 'stock'">
        <i class="fa-solid fa-warehouse"></i>
        <span>
          <strong>Stock by location</strong>
          <small>How much is in each warehouse — on-hand, committed, expiry</small>
        </span>
      </button>
    </div>

    <p v-if="error" class="banner error">{{ error }}</p>
    <p v-if="notice" class="banner ok">{{ notice }}</p>

    <div v-if="panel === 'items'" class="toolbar">
      <input
        v-model="filters.search"
        type="search"
        placeholder="Search SKU, item or barcode..."
        @keyup.enter="search"
      >
      <button class="btn" type="button" @click="search" :disabled="exporting">
        <i class="fa-solid fa-magnifying-glass"></i> Search
      </button>
      <select v-model="filters.category" @change="search">
        <option value="all">All categories</option>
        <option v-for="category in lookups.categories" :key="category" :value="category">
          {{ category }}
        </option>
      </select>
      <select v-model="filters.expiry" @change="search">
        <option value="all">All expiry</option>
        <option value="has">Has expiry</option>
        <option value="none">No expiry</option>
      </select>
      <select v-model="filters.hygiene" @change="search">
        <option value="all">All hygiene</option>
        <option v-for="bucket in hygieneBuckets" :key="bucket.key" :value="bucket.key">
          {{ bucket.label }}
        </option>
      </select>
      <button class="btn ghost" type="button" @click="reload" :disabled="exporting">Refresh</button>
      <button class="btn export" type="button" @click="exportCsv" :disabled="exporting || !total">
        <i class="fa-solid fa-file-export"></i> {{ exporting ? 'Exporting…' : 'Export CSV' }}
      </button>
    </div>

    <div v-else class="toolbar">
      <input
        v-model="filters.search"
        type="search"
        placeholder="Search SKU, item or warehouse..."
        @keyup.enter="search"
      >
      <button class="btn" type="button" @click="search" :disabled="exporting">
        <i class="fa-solid fa-magnifying-glass"></i> Search
      </button>
      <select v-model="filters.location" @change="search">
        <option value="all">All warehouses</option>
        <option v-for="location in lookups.locations" :key="location" :value="location">
          {{ location }}
        </option>
      </select>
      <select v-model="filters.group" @change="search">
        <option value="all">All groups</option>
        <option v-for="group in lookups.groups" :key="group.key" :value="group.key">
          {{ group.label }}
        </option>
      </select>
      <select v-model="filters.stock" @change="search">
        <option value="all">All stock</option>
        <option value="low">Reorder only</option>
      </select>
      <select v-model="filters.expiry" @change="search">
        <option value="all">All expiry</option>
        <option value="has">Has expiry</option>
        <option value="none">No expiry</option>
      </select>
      <select v-model="filters.hygiene" @change="search">
        <option value="all">All hygiene</option>
        <option v-for="bucket in hygieneBuckets" :key="bucket.key" :value="bucket.key">
          {{ bucket.label }}
        </option>
      </select>
      <select v-model="filters.category" @change="search">
        <option value="all">All categories</option>
        <option v-for="category in lookups.categories" :key="category" :value="category">
          {{ category }}
        </option>
      </select>
      <button class="btn ghost" type="button" @click="reload" :disabled="exporting">Refresh</button>
      <button class="btn export" type="button" @click="exportCsv" :disabled="exporting || !total">
        <i class="fa-solid fa-file-export"></i> {{ exporting ? 'Exporting…' : 'Export CSV' }}
      </button>
    </div>

    <div class="table-card" :class="panel">
      <div class="table-head">
        <h2>{{ panel === 'items' ? 'Product master (SAP item catalogue)' : 'Stock by warehouse' }}</h2>
        <p v-if="panel === 'items'">
          One row = one product (SKU). This is the catalogue — not a warehouse stock take.
          Total on-hand adds stock from every warehouse. Expiry = soonest batch date for that SKU.
        </p>
        <p v-else>
          One row = one product in one warehouse. Quantities and hygiene are for that location.
        </p>
      </div>

      <div v-if="panel === 'items'" class="stock-guide">
        <div><strong>SKU</strong> — SAP item code (<code>ItemCode</code>)</div>
        <div><strong>UOM</strong> — inventory unit; “Sale …” = sales unit if different</div>
        <div><strong>Conv / KIV</strong> — how many pieces per sales unit; KIV if not used</div>
        <div><strong>Cost</strong> — SAP moving average cost (<code>MovingAveragePrice</code>)</div>
        <div><strong>Selling</strong> — catalogue selling price (price list), not invoice billed price</div>
        <div><strong>Min / Max</strong> — SAP reorder levels. Shows — if SAP left them at 0 (not set)</div>
        <div><strong>Total on-hand</strong> — sum of stock across all warehouses for this SKU</div>
        <div><strong>On order</strong> — qty customers ordered but not yet fully delivered (item-level)</div>
        <div><strong>Nearest expiry</strong> — soonest SAP batch <code>ExpirationDate</code> for this SKU</div>
        <div><strong>Hygiene</strong> — months left until that nearest expiry. Use <em>Has expiry</em> to hide blanks.</div>
      </div>

      <div v-else class="stock-guide">
        <div><strong>On-hand</strong> — physical qty in that warehouse (SAP <code>InStock</code>)</div>
        <div><strong>Committed</strong> — reserved for open sales orders / picks (SAP <code>Committed</code>)</div>
        <div><strong>On order</strong> — still coming in from purchase / production (SAP <code>Ordered</code>)</div>
        <div><strong>Available</strong> — On-hand − Committed (what you can still issue)</div>
        <div><strong>Min</strong> — SAP reorder point. Shows — when SAP has none set.</div>
        <div><strong>Expiry / Hygiene</strong> — nearest batch expiry for the SKU · months remaining</div>
      </div>

      <table>
        <thead>
          <tr v-if="panel === 'items'">
            <th class="col-no">No</th>
            <th title="SAP ItemCode">SKU</th>
            <th>Item</th>
            <th>Barcode</th>
            <th>Category</th>
            <th title="Inventory UOM">UOM</th>
            <th title="Pieces per sales unit">
              <span class="th-main">Conv</span>
              <span class="th-sub">or KIV</span>
            </th>
            <th title="MovingAveragePrice">
              <span class="th-main">Cost</span>
              <span class="th-sub">avg cost</span>
            </th>
            <th title="Price list selling price">
              <span class="th-main">Selling</span>
              <span class="th-sub">list price</span>
            </th>
            <th title="SAP MinInventory">Min</th>
            <th title="SAP MaxInventory">Max</th>
            <th title="Sum of InStock across warehouses">
              <span class="th-main">Total on-hand</span>
              <span class="th-sub">all warehouses</span>
            </th>
            <th title="QuantityOrderedByCustomers">
              <span class="th-main">On order</span>
              <span class="th-sub">by customers</span>
            </th>
            <th title="Nearest batch ExpirationDate">
              <span class="th-main">Nearest expiry</span>
              <span class="th-sub">SAP batch</span>
            </th>
            <th title="Months remaining until nearest expiry">Hygiene</th>
          </tr>
          <tr v-else>
            <th class="col-no">No</th>
            <th>SKU</th>
            <th>Item</th>
            <th>UOM</th>
            <th>Warehouse</th>
            <th title="SAP InStock">On-hand</th>
            <th title="SAP Committed">Committed</th>
            <th title="SAP Ordered">On order</th>
            <th title="On-hand − Committed">Available</th>
            <th title="SAP MinInventory / MinimalStock">Min</th>
            <th>Stock</th>
            <th>Value</th>
            <th title="Nearest SAP batch ExpirationDate">Expiry</th>
            <th title="Months remaining until expiry">Hygiene</th>
          </tr>
        </thead>
        <tbody v-if="panel === 'items'">
          <tr v-for="(row, index) in items" :key="row.id">
            <td class="col-no">{{ rowNumber(index) }}</td>
            <td>{{ row.sku }}</td>
            <td>{{ row.itemName }}</td>
            <td>{{ row.barcode || '—' }}</td>
            <td>{{ row.category }}</td>
            <td>
              <div>{{ row.uom }}</div>
              <div v-if="row.salesUom && row.salesUom !== row.uom" class="sub">Sale {{ row.salesUom }}</div>
            </td>
            <td>{{ row.conversionFactor ?? 'KIV' }}</td>
            <td>{{ money(row.costPrice) }}</td>
            <td>{{ money(row.sellingPrice) }}</td>
            <td>{{ row.minQuantity > 0 ? number(row.minQuantity) : '—' }}</td>
            <td>{{ row.maxQuantity > 0 ? number(row.maxQuantity) : '—' }}</td>
            <td>
              <div>{{ number(row.stockQuantity) }}</div>
              <div class="sub">{{ number(row.locationCount) }} warehouse{{ row.locationCount === 1 ? '' : 's' }}</div>
            </td>
            <td>{{ number(row.orderedByCustomers) }}</td>
            <td>{{ row.expiryDate || '—' }}</td>
            <td>
              <span class="hygiene-pill" :class="row.hygieneBucket">{{ row.hygieneLabel }}</span>
            </td>
          </tr>
          <tr v-if="!items.length">
            <td colspan="15" class="empty">No products match these filters. Try All expiry, or pull from SAP B1.</td>
          </tr>
        </tbody>
        <tbody v-else>
          <tr v-for="(row, index) in stock" :key="row.id">
            <td class="col-no">{{ rowNumber(index) }}</td>
            <td>{{ row.sku }}</td>
            <td>{{ row.itemName }}</td>
            <td>
              <div>{{ row.uom || '—' }}</div>
              <div v-if="row.salesUom && row.salesUom !== row.uom" class="sub">Sale {{ row.salesUom }}</div>
            </td>
            <td>
              <div>{{ row.location }}</div>
              <div class="sub">{{ row.warehouseCode || row.locationGroupLabel }}</div>
            </td>
            <td>{{ number(row.quantity) }}</td>
            <td>{{ number(row.committed) }}</td>
            <td>{{ number(row.orderedQty) }}</td>
            <td>{{ number(row.available) }}</td>
            <td>{{ row.minQuantity > 0 ? number(row.minQuantity) : '—' }}</td>
            <td>
              <span class="stock-pill" :class="row.isLowStock ? 'low' : 'ok'">
                {{ row.isLowStock ? 'Reorder' : 'OK' }}
              </span>
            </td>
            <td>{{ money(row.inventoryValue) }}</td>
            <td>{{ row.expiryDate || '—' }}</td>
            <td>
              <span class="hygiene-pill" :class="row.hygieneBucket">{{ row.hygieneLabel }}</span>
            </td>
          </tr>
          <tr v-if="!stock.length">
            <td colspan="14" class="empty">No stock rows match these filters. Try All expiry, or pull from SAP B1.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="pager">
      <label class="page-size">
        Rows
        <select v-model.number="pageSize">
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
      </label>
      <span>{{ total }} {{ panel === 'items' ? 'products' : 'stock rows' }}</span>
      <button type="button" :disabled="page <= 1" @click="page -= 1">Previous</button>
      <span>Page {{ page }}</span>
      <button type="button" :disabled="page * pageSize >= total" @click="page += 1">Next</button>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, watch } from 'vue'
import warehouseApi, { downloadWarehouseCsv } from '@/utils/warehouseApi'

const panel = ref('items')
const error = ref('')
const notice = ref('')
const exporting = ref(false)
const page = ref(1)
const pageSize = ref(20)
const total = ref(0)
const items = ref([])
const stock = ref([])
const lookups = ref({ sources: [], categories: [], locations: [], items: [], uoms: [], groups: [] })
const hygieneBuckets = [
  { key: 'expired', label: 'Expired' },
  { key: 'lt_3m', label: 'Expires < 3 M' },
  { key: 'lt_6m', label: 'Expires < 6 M' },
  { key: 'm6_12', label: 'Expires 6–12 M' },
  { key: 'm12_18', label: 'Expires 12–18 M' },
  { key: 'gt_18m', label: 'Expires > 18 M' },
  { key: 'unknown', label: 'No expiry' }
]
const filters = reactive({
  search: '',
  source: 'all',
  category: 'all',
  location: 'all',
  group: 'all',
  stock: 'all',
  expiry: 'all',
  hygiene: 'all'
})

function money(value) {
  return `RM ${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function number(value) {
  return Number(value || 0).toLocaleString()
}

function rowNumber(index) {
  return (page.value - 1) * pageSize.value + index + 1
}

async function loadLookups() {
  const { data } = await warehouseApi.get('/api/warehouse/lookups')
  lookups.value = {
    sources: [],
    categories: [],
    locations: [],
    items: [],
    uoms: [],
    groups: [],
    ...data
  }
}

async function reload() {
  try {
    error.value = ''
    notice.value = ''
    const params = { ...filters, page: page.value, pageSize: pageSize.value }
    if (panel.value === 'items') {
      const { data } = await warehouseApi.get('/api/warehouse/items', { params })
      items.value = data.items || []
      total.value = data.total || 0
    } else {
      const { data } = await warehouseApi.get('/api/warehouse/stock', { params })
      stock.value = data.rows || []
      total.value = data.total || 0
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to load inventory.'
  }
}

function search() {
  if (page.value !== 1) page.value = 1
  else reload()
}

async function exportCsv() {
  try {
    exporting.value = true
    error.value = ''
    const path = panel.value === 'items' ? '/api/warehouse/export/items' : '/api/warehouse/export/stock'
    const name = panel.value === 'items' ? 'warehouse-products.csv' : 'warehouse-stock.csv'
    await downloadWarehouseCsv(path, { ...filters }, name)
    notice.value = 'CSV export downloaded.'
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to export CSV.'
  } finally {
    exporting.value = false
  }
}

watch(panel, () => {
  page.value = 1
  reload()
})

watch(page, reload)
watch(pageSize, () => {
  if (page.value !== 1) page.value = 1
  else reload()
})

onMounted(async () => {
  await loadLookups()
  await reload()
})
</script>

<style scoped>
.mode-switch {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 14px;
}

.mode-switch button {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  text-align: left;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: white;
  cursor: pointer;
}

.mode-switch button i {
  margin-top: 2px;
  color: #64748b;
}

.mode-switch button span {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mode-switch button strong {
  font-size: 0.9rem;
  color: #0f172a;
}

.mode-switch button small {
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.35;
}

.mode-switch button.active {
  border-color: #0ea5e9;
  background: #f0f9ff;
  box-shadow: 0 0 0 1px #0ea5e9;
}

.mode-switch button.active i,
.mode-switch button.active strong {
  color: #0369a1;
}

.toolbar, .pager {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 12px;
}

.toolbar input, .toolbar select, .btn, .pager button, .page-size select {
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: white;
  font-size: 0.813rem;
}

.btn {
  background: #0ea5e9;
  color: white;
  border-color: #0ea5e9;
  cursor: pointer;
}

.btn.ghost {
  background: white;
  color: #475569;
}

.btn.export {
  background: #059669;
  border-color: #059669;
}

.btn i {
  margin-right: 4px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.banner {
  padding: 10px 12px;
  border-radius: 6px;
  margin-bottom: 12px;
  font-size: 0.813rem;
}

.banner.error { background: #fef2f2; color: #b91c1c; }
.banner.ok { background: #ecfdf5; color: #047857; }

.table-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: auto;
}

.table-head {
  padding: 14px 16px 8px;
}

.table-head h2 {
  margin: 0 0 4px;
  font-size: 1rem;
  color: #0f172a;
}

.table-head p {
  margin: 0;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.4;
}

.table-card.items {
  border-top: 3px solid #0ea5e9;
}

.table-card.stock {
  border-top: 3px solid #059669;
}

.stock-guide {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px 16px;
  margin: 0 12px 12px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.72rem;
  color: #475569;
  line-height: 1.4;
}

.stock-guide code {
  font-size: 0.68rem;
  background: #e2e8f0;
  padding: 1px 4px;
  border-radius: 3px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 10px 8px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.78rem;
  text-align: left;
  vertical-align: top;
}

th { background: #f8fafc; color: #475569; }

.th-main {
  display: block;
  font-weight: 600;
  color: #334155;
}

.th-sub {
  display: block;
  font-size: 0.66rem;
  font-weight: 400;
  color: #94a3b8;
  margin-top: 1px;
}

.col-no {
  width: 44px;
  text-align: center;
  white-space: nowrap;
}

.empty { text-align: center; color: #64748b; padding: 24px; }

.sub {
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: 2px;
}

.stock-pill {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
}

.stock-pill.ok { background: #dcfce7; color: #166534; }
.stock-pill.low { background: #ffedd5; color: #9a3412; }

.hygiene-pill {
  display: inline-flex;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;
  white-space: nowrap;
  background: #f1f5f9;
  color: #64748b;
}

.hygiene-pill.expired { background: #7f1d1d; color: #fecaca; }
.hygiene-pill.lt_3m { background: #fee2e2; color: #991b1b; }
.hygiene-pill.lt_6m { background: #ffedd5; color: #9a3412; }
.hygiene-pill.m6_12 { background: #fef9c3; color: #854d0e; }
.hygiene-pill.m12_18 { background: #ecfccb; color: #3f6212; }
.hygiene-pill.gt_18m { background: #dcfce7; color: #166534; }
.hygiene-pill.unknown { background: #f1f5f9; color: #64748b; }

.pager { justify-content: flex-end; color: #64748b; font-size: 0.813rem; }
.page-size { display: flex; align-items: center; gap: 6px; margin-right: auto; }

@media (max-width: 900px) {
  .mode-switch,
  .stock-guide {
    grid-template-columns: 1fr;
  }
}
</style>
